/**
 * Utility to format distances based on locale
 */

// Detect if the user is in a locale that uses miles
export function usesMiles(): boolean {
  // Countries that primarily use miles
  const mileCountries = ["US", "LR", "MM"];

  try {
    // Get user's locale
    const locale = navigator.language;
    const region = new Intl.Locale(locale).region;

    return region ? mileCountries.includes(region) : false;
  } catch (error) {
    // Default to metric if there's an error
    return false;
  }
}

// Format swim distance
export function formatSwimDistance(distanceKm: number): string {
  if (usesMiles()) {
    // Convert to yards for US (approximate)
    if (distanceKm === 3.8) return "2.4mi";
    if (distanceKm === 1.9) return "1.2mi";
    if (distanceKm === 1.5) return "0.93mi";
    if (distanceKm === 0.75) return "0.47mi";

    // Generic conversion
    const miles = (distanceKm * 0.621371).toFixed(1);
    return `${miles}mi`;
  }

  // Use km for metric
  return `${distanceKm}k`;
}

// Format bike distance
export function formatBikeDistance(distanceKm: number): string {
  if (usesMiles()) {
    // Convert to miles for US
    if (distanceKm === 180) return "112mi";
    if (distanceKm === 90) return "56mi";
    if (distanceKm === 40) return "25mi";
    if (distanceKm === 20) return "12.4mi";

    // Generic conversion
    const miles = Math.round(distanceKm * 0.621371);
    return `${miles}mi`;
  }

  // Use km for metric
  return `${distanceKm}k`;
}

// Format run distance
export function formatRunDistance(distanceKm: number): string {
  if (usesMiles()) {
    // Special cases for marathon and half marathon
    if (distanceKm === 42.2) return "marathon";
    if (distanceKm === 21.1) return "half marathon";
    if (distanceKm === 10) return "6.2mi run";
    if (distanceKm === 5) return "3.1mi run";

    // Generic conversion
    const miles = (distanceKm * 0.621371).toFixed(1);
    return `${miles}mi`;
  }

  // Special cases for metric
  if (distanceKm === 42.2) return "marathon";
  if (distanceKm === 21.1) return "half marathon";

  // Use km for other metric distances
  return `${distanceKm}k run`;
}

// Get full distance description
export function getDistanceDescription(distance: string): string {
  switch (distance) {
    case "long":
      return `Typically: ${formatSwimDistance(3.8)} swim, ${formatBikeDistance(
        180
      )} bike, ${formatRunDistance(42.2)}`;
    case "middle":
      return `Typically: ${formatSwimDistance(1.9)} swim, ${formatBikeDistance(
        90
      )} bike, ${formatRunDistance(21.1)}`;
    case "olympic":
      return `Typically: ${formatSwimDistance(1.5)} swim, ${formatBikeDistance(
        40
      )} bike, ${formatRunDistance(10)}`;
    case "sprint":
      return `Typically: ${formatSwimDistance(0.75)} swim, ${formatBikeDistance(
        20
      )} bike, ${formatRunDistance(5)}`;
    case "other":
      return "Other triathlon distances or formats";
    default:
      return "";
  }
}
